import { useState, useEffect, useMemo } from 'react';
import { Lock, Plus, Trash2, Play, Video, Calendar, Clock, ExternalLink, Edit2, X, Save, Users, DollarSign, UserCheck, Tag, Copy, Filter, User, FileCheck, FileText, Download, Loader2, Gift, Store, Sparkles, MessageCircle, MapPin, Radio, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import CodesTab from './admin/CodesTab';
import VideosTab from './admin/VideosTab';
import RecordingsTab from './admin/RecordingsTab';
import AgentesTab from './admin/AgentesTab';
import Codigos500Tab from './admin/Codigos500Tab';

const SECRETOS_OPTIONS = [
  { value: "Secreto #1", label: "Secreto #1 - Certificados de Viaje" },
  { value: "Secreto #2", label: "Secreto #2 - Asesorías Migratorias" },
  { value: "Secreto #3", label: "Secreto #3 - Plataformas B2B" },
  { value: "Secreto #4", label: "Secreto #4 - Club Vacacional Digital" },
  { value: "Secreto #5", label: "Secreto #5 - Embajador Digital" },
  { value: "Organizador", label: "Organizador de Tiempos" },
];

// Opciones para grabaciones: etiquetas alineadas con los títulos de curso en la Academia
const RECORDING_CURSO_OPTIONS = [
  { value: "Secreto #1", label: "El Paquete del Millón (Certificados)" },
  { value: "Secreto #2", label: "Visa con Éxito (Migratorias)" },
  { value: "Secreto #3", label: "Academia B2B (Plataformas)" },
  { value: "Secreto #4", label: "Curso Profesional de Ventas (Club Vacacional)" },
  { value: "Secreto #5", label: "El Poder de Referir (Embajador Digital)" },
];

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Mexico_City", label: "Ciudad de México" },
  { value: "America/Bogota", label: "Colombia (COT)" },
  { value: "America/Lima", label: "Perú (PET)" },
  { value: "America/Santiago", label: "Chile (CLT)" },
  { value: "America/Buenos_Aires", label: "Argentina (ART)" },
  { value: "Europe/Madrid", label: "España (CET)" },
];

export const AdminPanel = ({ isOpen, onClose, skipAuth = false, renderMode = 'sheet' }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Auto-authenticate when skipAuth is true; restore session otherwise
  useEffect(() => {
    if (skipAuth && isOpen && !isAuthenticated) {
      setIsAuthenticated(true);
      setPassword('skipauth');
      return;
    }
    if (!skipAuth && !isAuthenticated) {
      try {
        if (sessionStorage.getItem('cv_admin_token')) setIsAuthenticated(true);
      } catch (e) { /* ignore */ }
    }
  }, [skipAuth, isOpen, isAuthenticated]);
  const [tutorials, setTutorials] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [events, setEvents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [orlandoLeads, setOrlandoLeads] = useState([]);
  const [investorLeads, setInvestorLeads] = useState([]);
  const [cursoAccess, setCursoAccess] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [certificados, setCertificados] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [newAgente, setNewAgente] = useState({ nombre: '', codigo_ref: '' });
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [codeQuantity, setCodeQuantity] = useState(1);
  const [codeAmount, setCodeAmount] = useState(200);
  const [loading, setLoading] = useState(false);
  const [tutorialLoading, setTutorialLoading] = useState(false);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [eventLoading, setEventLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('events');
  const [leadsSubTab, setLeadsSubTab] = useState('general'); // general | orlando | inversores | curso
  
  // Lead filters
  const [leadFilterAgent, setLeadFilterAgent] = useState('all');
  const [leadFilterPlan, setLeadFilterPlan] = useState('all');
  const [leadFilterStatus, setLeadFilterStatus] = useState('all');
  const [leadFilterOrigen, setLeadFilterOrigen] = useState('all');

  // Lead edit/delete state
  const [editingLead, setEditingLead] = useState(null);
  const [editLeadData, setEditLeadData] = useState({});

  // Memoized filter options to avoid recalculating on every render
  const filterOrigenes = useMemo(() => [...new Set(leads.map(l => l.origen || 'Precios'))].filter(o => o).sort(), [leads]);
  const filterAgents = useMemo(() => [...new Set(leads.filter(l => l.fue_referido && l.nombre_agente_referidor).map(l => l.nombre_agente_referidor))].sort(), [leads]);
  const filterPlans = useMemo(() => [...new Set(leads.map(l => l.plan_seleccionado))].filter(p => p).sort(), [leads]);
  
  // Edit event state
  const [editingEvent, setEditingEvent] = useState(null);

  // Codigos 500 state
  const [codigos500, setCodigos500] = useState([]);
  
  // New tutorial form
  const [newTutorial, setNewTutorial] = useState({
    secreto: '',
    title: '',
    url: '',
    description: '',
    location: 'home',
    recorded_date: '',
    presenter: '',
    thumbnail: '',
    order: 0
  });
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  // New recording (grabación en vivo / Zoom) form
  const [newRecording, setNewRecording] = useState({
    title: '',
    url: '',
    recorded_date: '',
    presenter: '',
    secreto: '',
    description: ''
  });

  // New event form
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    timezone: 'America/New_York',
    platform: 'zoom',
    zoom_link: '',
    cta_mode: 'buttons',
    whatsapp_number: '19548059212',
    recurrence: 'once',
    location: 'home'
  });

  // Load data when authenticated - with retry and polling
  useEffect(() => {
    if (isAuthenticated) {
      const loadAll = () => {
        loadTutorials();
        loadRecordings();
        loadEvents();
        loadLeads();
        loadDiscountCodes();
        loadAgentes();
        loadCodigos500();
      };
      loadAll();
      // Retry after 3 seconds in case backend was still starting
      const retryTimer = setTimeout(loadAll, 3000);
      // Auto-refresh every 2 minutes to keep data fresh
      const pollInterval = setInterval(loadAll, 2 * 60 * 1000);
      return () => { clearTimeout(retryTimer); clearInterval(pollInterval); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Admin data cache helpers (7-day TTL)
  const getCached = (key) => {
    try {
      const raw = localStorage.getItem(`cv_admin_${key}`);
      if (raw) {
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000) return data;
      }
    } catch (e) {
      console.error('Failed to read admin cache:', e);
    }
    return null;
  };
  const setCache = (key, data) => {
    try { localStorage.setItem(`cv_admin_${key}`, JSON.stringify({ data, ts: Date.now() })); } catch (e) {
      console.error('Failed to set admin cache:', e);
    }
  };

  const loadTutorials = async () => {
    const MAX_RETRIES = 3;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(`${API_URL}/api/tutorials`, { cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setTutorials(data);
            setCache('tutorials', data);
          }
          return;
        }
        if (response.status < 500) break;
      } catch (e) {
        console.error(`Tutorials attempt ${attempt + 1} failed:`, e);
      }
      if (attempt < MAX_RETRIES - 1) {
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    setTutorials(prev => {
      if (prev.length === 0) {
        const cached = getCached('tutorials');
        return cached || [];
      }
      return prev;
    });
  };

  const loadRecordings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recordings`, { cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) setRecordings(data);
      }
    } catch (e) { console.error('Recordings fetch failed:', e); }
  };

  const loadEvents = async () => {    try {
      const response = await fetch(`${API_URL}/api/admin/events`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setEvents(data);
          setCache('events', data);
        }
        return;
      }
    } catch (e) { console.error('Admin events fetch failed:', e); }
    // Fallback 1: try public endpoint
    try {
      const response = await fetch(`${API_URL}/api/events`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setEvents(data);
          setCache('events', data);
        }
        return;
      }
    } catch (e) { console.error('Public events fetch failed:', e); }
    // Fallback 2: use local cache ONLY if events are empty
    setEvents(prev => {
      if (prev.length === 0) {
        const cached = getCached('events');
        return cached || [];
      }
      return prev;
    });
  };

  const loadLeads = async () => {
    // Load both purchase leads + Orlando-gift leads in parallel
    try {
      const [purchase, orlando] = await Promise.allSettled([
        fetch(`${API_URL}/api/admin/leads`).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/leads/orlando?limit=500`).then(r => r.ok ? r.json() : { items: [] }),
      ]);
      const purchaseData = purchase.status === 'fulfilled' && Array.isArray(purchase.value) ? purchase.value : [];
      const orlandoData = orlando.status === 'fulfilled' ? (orlando.value?.items || []) : [];
      setLeads(purchaseData);
      setCache('leads', purchaseData);
      setOrlandoLeads(orlandoData);
      return;
    } catch (e) { console.error('Leads fetch failed:', e); }
    setLeads(prev => {
      if (prev.length === 0) {
        const cached = getCached('leads');
        return cached || [];
      }
      return prev;
    });
  };

  const loadInvestorLeads = async () => {
    try {
      const r = await fetch(`${API_URL}/api/leads/investor?limit=500`);
      if (r.ok) {
        const data = await r.json();
        setInvestorLeads(data?.items || []);
      }
    } catch (e) { console.error('Investor leads fetch failed:', e); }
  };

  const [manualFreeLeads, setManualFreeLeads] = useState([]);
  const loadManualFreeLeads = async () => {
    try {
      const r = await fetch(`${API_URL}/api/leads/manual-free?limit=500`);
      if (r.ok) {
        const data = await r.json();
        setManualFreeLeads(data?.items || []);
      }
    } catch (e) { console.error('Manual-free leads fetch failed:', e); }
  };

  const [opcLeads, setOpcLeads] = useState([]);
  const loadOpcLeads = async () => {
    try {
      const r = await fetch(`${API_URL}/api/leads/opc?limit=500`);
      if (r.ok) {
        const data = await r.json();
        setOpcLeads(data?.items || []);
      }
    } catch (e) { console.error('OPC leads fetch failed:', e); }
  };

  const loadCursoAccess = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/curso/access`);
      if (r.ok) {
        const data = await r.json();
        setCursoAccess(Array.isArray(data) ? data : []);
      }
    } catch (e) { console.error('Curso access fetch failed:', e); }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('¿Estás seguro de eliminar este lead?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/${encodeURIComponent(leadId)}`, { method: 'DELETE' });
      if (res.ok) {
        setLeads(prev => prev.filter(l => (l.id || l.email) !== leadId));
        toast.success('Lead eliminado');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Error al eliminar');
      }
    } catch {
      toast.error('Error de conexion');
    }
  };

  const saveLeadEdit = async () => {
    if (!editingLead) return;
    const leadId = editingLead.id || editingLead.email;
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/${encodeURIComponent(leadId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editLeadData)
      });
      if (res.ok) {
        setLeads(prev => prev.map(l => (l.id || l.email) === leadId ? { ...l, ...editLeadData } : l));
        setEditingLead(null);
        toast.success('Lead actualizado');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Error al actualizar');
      }
    } catch {
      toast.error('Error de conexion');
    }
  };

  const startEditLead = (lead) => {
    setEditingLead(lead);
    setEditLeadData({
      nombre: lead.nombre || '',
      apellido: lead.apellido || '',
      email: lead.email || '',
      telefono: lead.telefono || '',
      pais: lead.pais || '',
      plan_seleccionado: lead.plan_seleccionado || '',
      estado_pago: lead.estado_pago || '',
      nombre_agente_referidor: lead.nombre_agente_referidor || ''
    });
  };

  const loadDiscountCodes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/codes`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setDiscountCodes(data);
          setCache('codes', data);
        }
        return;
      }
    } catch (e) { console.error('Codes fetch failed:', e); }
    setDiscountCodes(prev => {
      if (prev.length === 0) {
        const cached = getCached('codes');
        return cached || [];
      }
      return prev;
    });
  };

  const loadCertificados = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/certificados`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setCertificados(data);
          setCache('certificados', data);
        }
        return;
      }
    } catch (e) { console.error('Certificados fetch failed:', e); }
    setCertificados(prev => {
      if (prev.length === 0) {
        const cached = getCached('certificados');
        return cached || [];
      }
      return prev;
    });
  };

  const loadAgentes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/agentes`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) setAgentes(data);
      }
    } catch (e) { console.error('Agentes fetch failed:', e); }
  };

  const loadCodigos500 = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/codigos-viaje`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) setCodigos500(data);
      }
    } catch (e) { console.error('Codigos 500 fetch failed:', e); }
  };

  const createAgente = async () => {
    if (!newAgente.nombre || !newAgente.codigo_ref) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/agentes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAgente)
      });
      if (response.ok) {
        setNewAgente({ nombre: '', codigo_ref: '' });
        loadAgentes();
      } else {
        const data = await response.json();
        alert(data.detail || 'Error al crear agente');
      }
    } catch { alert('Error de conexion'); }
  };

  const deleteAgente = async (id) => {
    if (!window.confirm('Eliminar este agente?')) return;
    try {
      await fetch(`${API_URL}/api/admin/agentes/${id}`, { method: 'DELETE' });
      loadAgentes();
    } catch { /* error */ }
  };

  const generateCodes = async () => {
    setGeneratingCodes(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/codes?amount=${codeAmount}&quantity=${codeQuantity}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.quantity} código(s) generado(s)`);
        loadDiscountCodes();
      }
    } catch {
      toast.error('Error al generar códigos');
    }
    setGeneratingCodes(false);
  };

  const handleLogin = async () => {
    setLoading(true);
    const trimmedPassword = password.trim();
    const trimmedUsername = username.trim();
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: trimmedUsername, password: trimmedPassword })
      });
      
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        try { sessionStorage.setItem('cv_admin_token', data.token || '1'); } catch (e) { /* ignore */ }
        setPassword(trimmedPassword);
        setIsAuthenticated(true);
        toast.success('Acceso concedido');
      } else {
        toast.error('Contraseña incorrecta');
      }
    } catch {
      toast.error('Error de conexion. Intenta de nuevo.');
    }
    setLoading(false);
  };

  const handleThumbnailUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecciona una imagen (JPG, PNG o WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5 MB');
      return;
    }
    setThumbnailUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('kind', 'portada');
      const response = await fetch(`${API_URL}/api/mural/upload`, { method: 'POST', body: formData });
      if (response.ok) {
        const data = await response.json();
        setNewTutorial(prev => ({ ...prev, thumbnail: `${API_URL}${data.url}` }));
        toast.success('Portada subida');
      } else {
        toast.error('No se pudo subir la portada');
      }
    } catch {
      toast.error('Error de conexion al subir');
    }
    setThumbnailUploading(false);
  };

  const handleAddTutorial = async () => {
    if (!newTutorial.title || !newTutorial.url || (newTutorial.location !== 'academia' && !newTutorial.secreto)) {
      toast.error('Completa los campos requeridos');
      return;
    }

    setTutorialLoading(true);
    let lastError = 'Error de conexion';
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await fetch(`${API_URL}/api/admin/tutorials`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newTutorial)
        });

        if (response.ok) {
          toast.success('Video agregado');
          setTimeout(() => setNewTutorial(prev => ({ secreto: prev.secreto, title: '', url: '', description: '', location: prev.location, recorded_date: '', presenter: '', thumbnail: '', order: 0 })), 0);
          loadTutorials();
          setTutorialLoading(false);
          return;
        }
        const err = await response.json().catch(() => ({}));
        lastError = err.detail || 'Error al agregar video';
        if (response.status >= 400 && response.status < 500) break;
      } catch {
        lastError = 'Error de conexion. Reintentando...';
      }
      if (attempt < 2) {
        toast.info(`Reintentando... (${attempt + 2}/3)`);
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    toast.error(lastError);
    setTutorialLoading(false);
  };

  const handleDeleteTutorial = async (tutorialId) => {
    if (!window.confirm('¿Eliminar este video?')) return;

    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await fetch(
          `${API_URL}/api/admin/tutorials/${tutorialId}`,
          { method: 'DELETE' }
        );

        if (response.ok) {
          toast.success('Video eliminado');
          loadTutorials();
          return;
        }
        if (response.status >= 400 && response.status < 500) {
          toast.error('Error al eliminar');
          return;
        }
      } catch { /* retry */ }
      if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
    }
    toast.error('Error de conexion. Intenta de nuevo.');
  };

  const handleAddRecording = async () => {
    if (!newRecording.title || !newRecording.url || !newRecording.recorded_date) {
      toast.error('Completa título, enlace y fecha');
      return;
    }
    setRecordingLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/recordings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRecording)
      });
      if (response.ok) {
        toast.success('Grabación agregada');
        setNewRecording(prev => ({ title: '', url: '', recorded_date: '', presenter: '', secreto: prev.secreto, description: '' }));
        loadRecordings();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Error al agregar grabación');
      }
    } catch {
      toast.error('Error de conexion');
    }
    setRecordingLoading(false);
  };

  const handleDeleteRecording = async (recordingId) => {
    if (!window.confirm('¿Eliminar esta grabación?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/recordings/${recordingId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Grabación eliminada');
        loadRecordings();
      } else {
        toast.error('Error al eliminar');
      }
    } catch {
      toast.error('Error de conexion');
    }
  };

  // Event handlers
  const handleAddEvent = async () => {
    if (!newEvent.title || !newEvent.event_date || !newEvent.event_time || !newEvent.zoom_link) {
      toast.error('Completa los campos requeridos');
      return;
    }

    setEventLoading(true);
    let lastError = 'Error de conexion';
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await fetch(`${API_URL}/api/admin/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newEvent)
        });

        if (response.ok) {
          toast.success('Evento agregado');
          setNewEvent({ title: '', description: '', event_date: '', event_time: '', timezone: 'America/New_York', platform: 'zoom', zoom_link: '', cta_mode: 'buttons', whatsapp_number: '19548059212', recurrence: 'once', location: 'home' });
          loadEvents();
          setEventLoading(false);
          return;
        }
        const err = await response.json().catch(() => ({}));
        lastError = err.detail || 'Error al agregar evento';
        if (response.status >= 400 && response.status < 500) break;
      } catch {
        lastError = 'Error de conexion. Reintentando...';
      }
      if (attempt < 2) {
        toast.info(`Reintentando... (${attempt + 2}/3)`);
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    toast.error(lastError);
    setEventLoading(false);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('¿Eliminar este evento?')) return;

    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await fetch(
          `${API_URL}/api/admin/events/${eventId}`,
          { method: 'DELETE' }
        );

        if (response.ok) {
          toast.success('Evento eliminado');
          loadEvents();
          return;
        }
        if (response.status >= 400 && response.status < 500) {
          toast.error('Error al eliminar');
          return;
        }
      } catch { /* retry */ }
      if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
    }
    toast.error('Error de conexion. Intenta de nuevo.');
  };

  // Edit event handlers
  const handleEditEvent = (event) => {
    setEditingEvent({ ...event });
  };

  const handleToggleEventActive = async (event) => {
    const newActive = !event.is_active;
    try {
      const response = await fetch(
        `${API_URL}/api/admin/events/${event.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_active: newActive })
        }
      );
      if (response.ok) {
        toast.success(newActive ? 'Evento EN VIVO (visible)' : 'Evento OCULTO');
        loadEvents();
      } else {
        toast.error('No se pudo cambiar la visibilidad');
      }
    } catch (e) {
      toast.error('Error de conexión');
    }
  };

  const handleCancelEdit = () => {
    setEditingEvent(null);
  };

  const handleSaveEvent = async () => {
    if (!editingEvent.title || !editingEvent.event_date || !editingEvent.event_time || !editingEvent.zoom_link) {
      toast.error('Completa los campos requeridos');
      return;
    }

    setEventLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/events/${editingEvent.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: editingEvent.title,
            description: editingEvent.description,
            event_date: editingEvent.event_date,
            event_time: editingEvent.event_time,
            timezone: editingEvent.timezone,
            platform: editingEvent.platform || 'zoom',
            zoom_link: editingEvent.zoom_link,
            cta_mode: editingEvent.cta_mode || 'buttons',
            whatsapp_number: editingEvent.whatsapp_number || '19548059212',
            recurrence: editingEvent.recurrence || 'once',
            location: editingEvent.location || 'home'
          })
        }
      );

      if (response.ok) {
        toast.success('Evento actualizado');
        setEditingEvent(null);
        loadEvents();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Error al actualizar evento');
      }
    } catch (e) {
      console.error('Save event error:', e);
      toast.error('Error de conexión al guardar evento');
    }
    setEventLoading(false);
  };

  const handleClose = () => {
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setTutorials([]);
    setEvents([]);
    setLeads([]);
    setDiscountCodes([]);
    setEditingEvent(null);
    setNewTutorial({ secreto: '', title: '', url: '', description: '', location: 'home', recorded_date: '', presenter: '', thumbnail: '', order: 0 });
    setNewEvent({ title: '', description: '', event_date: '', event_time: '', timezone: 'America/New_York', platform: 'zoom', zoom_link: '', recurrence: 'once', location: 'home' });
    onClose();
  };

  // Group tutorials by secreto
  const groupedTutorials = tutorials.reduce((acc, tutorial) => {
    const key = tutorial.secreto;
    if (!acc[key]) acc[key] = [];
    acc[key].push(tutorial);
    return acc;
  }, {});

  // The admin tabs content (shared between page and sheet mode)
  const adminContent = (
    <Tabs value={activeTab} onValueChange={(val) => {
      setActiveTab(val);
      if (val === 'events') loadEvents();
      if (val === 'leads') {
        loadLeads();
      }
      if (val === 'codes') loadDiscountCodes();
      if (val === 'videos') loadTutorials();
      if (val === 'agentes') loadAgentes();
      if (val === 'codigos500') loadCodigos500();
    }} className="py-4">
      <TabsList className="grid w-full grid-cols-4 md:grid-cols-4 mb-6 h-auto">
        <TabsTrigger value="events" className="flex items-center gap-1 text-xs sm:text-sm">
          <Calendar className="h-4 w-4" />
          <span className="hidden sm:inline">Eventos</span>
        </TabsTrigger>
        <TabsTrigger value="videos" className="flex items-center gap-1 text-xs sm:text-sm">
          <Video className="h-4 w-4" />
          <span className="hidden sm:inline">Videos</span>
        </TabsTrigger>
        <TabsTrigger value="agentes" className="flex items-center gap-1 text-xs sm:text-sm">
          <UserCheck className="h-4 w-4" />
          <span className="hidden sm:inline">Agentes</span>
        </TabsTrigger>
        <TabsTrigger value="contrato" data-testid="admin-tab-contrato" className="flex items-center gap-1 text-xs sm:text-sm">
          <FileText className="h-4 w-4" />
          <span className="hidden sm:inline">Contrato</span>
        </TabsTrigger>
      </TabsList>

            {/* Contrato Tab */}
            <TabsContent value="contrato" className="space-y-6">
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#151B2B] p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#45A0EE] flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-black text-[#1D1D1B] dark:text-white mb-2">Contratos de Agente</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                  Crea el contrato de afiliación con el membrete oficial, descárgalo en PDF y envíalo por email. Cada contrato registra automáticamente al agente en la pestaña <strong>Agentes</strong>, donde queda guardado su PDF.
                </p>
                <a
                  href="/contrato.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admin-contrato-link"
                  className="inline-flex items-center gap-2 bg-[#45A0EE] hover:bg-[#3590de] text-white font-bold px-6 py-3 rounded-full transition-colors"
                >
                  <FileText className="h-4 w-4" />
                  Abrir generador de contratos
                </a>
              </div>
            </TabsContent>

            {/* Events Tab */}
            <TabsContent value="events" className="space-y-6">
              {/* Add New Event */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-[#1D1D1B] dark:text-white flex items-center gap-2 mb-4">
                  <Plus className="h-4 w-4 text-[#80C753]" />
                  Agregar Nuevo Evento
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm">Título del Evento *</Label>
                    <Input
                      placeholder="Ej: Webinar de Capacitación Mensual"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                      className="bg-white dark:bg-slate-800"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Fecha *</Label>
                    <Input
                      type="date"
                      value={newEvent.event_date}
                      onChange={(e) => setNewEvent({...newEvent, event_date: e.target.value})}
                      className="bg-white dark:bg-slate-800"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Hora *</Label>
                    <Input
                      type="time"
                      value={newEvent.event_time}
                      onChange={(e) => setNewEvent({...newEvent, event_time: e.target.value})}
                      className="bg-white dark:bg-slate-800"
                    />
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm">Zona Horaria del Evento</Label>
                    <Select
                      value={newEvent.timezone}
                      onValueChange={(value) => setNewEvent({...newEvent, timezone: value})}
                    >
                      <SelectTrigger className="bg-white dark:bg-slate-800">
                        <SelectValue placeholder="Selecciona zona horaria" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm">Plataforma del Evento *</Label>
                    <Select value={newEvent.platform} onValueChange={(value) => setNewEvent({...newEvent, platform: value})}>
                      <SelectTrigger className="bg-white dark:bg-slate-800">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="zoom">Zoom</SelectItem>
                        <SelectItem value="tiktok">TikTok Live</SelectItem>
                        <SelectItem value="youtube">YouTube Live</SelectItem>
                        <SelectItem value="facebook">Facebook Live</SelectItem>
                        <SelectItem value="instagram">Instagram Live</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm">
                      {newEvent.platform === 'zoom' ? 'Link de Zoom' :
                       newEvent.platform === 'tiktok' ? 'Link de TikTok Live' :
                       newEvent.platform === 'youtube' ? 'Link de YouTube Live' :
                       newEvent.platform === 'facebook' ? 'Link de Facebook Live' :
                       newEvent.platform === 'instagram' ? 'Link de Instagram Live' :
                       'Link del Evento'} *
                    </Label>
                    <Input
                      placeholder={
                        newEvent.platform === 'zoom' ? 'https://zoom.us/j/...' :
                        newEvent.platform === 'tiktok' ? 'https://www.tiktok.com/@usuario/live' :
                        newEvent.platform === 'youtube' ? 'https://youtube.com/live/...' :
                        newEvent.platform === 'facebook' ? 'https://facebook.com/...' :
                        newEvent.platform === 'instagram' ? 'https://instagram.com/...' :
                        'https://...'
                      }
                      value={newEvent.zoom_link}
                      onChange={(e) => setNewEvent({...newEvent, zoom_link: e.target.value})}
                      className="bg-white dark:bg-slate-800"
                    />
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm">Recurrencia (auto-rolling semanal)</Label>
                    <Select value={newEvent.recurrence || 'once'} onValueChange={(value) => setNewEvent({...newEvent, recurrence: value})}>
                      <SelectTrigger className="bg-white dark:bg-slate-800" data-testid="event-recurrence-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="once">Una sola vez (fecha fija)</SelectItem>
                        <SelectItem value="weekly_mon">Cada lunes (auto rota cada semana)</SelectItem>
                        <SelectItem value="weekly_tue">Cada martes (auto rota cada semana)</SelectItem>
                        <SelectItem value="weekly_wed">Cada miércoles (auto rota cada semana)</SelectItem>
                        <SelectItem value="weekly_thu">Cada jueves (auto rota cada semana)</SelectItem>
                        <SelectItem value="weekly_sat">Cada sábado (auto rota cada semana)</SelectItem>
                        <SelectItem value="daily_weekdays">Diario Lun–Vie (rota al siguiente día hábil; viernes → lunes)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500">
                      Si eliges semanal, la fecha pública avanza al próximo miércoles/sábado automáticamente 3h después del inicio.
                    </p>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm">¿Dónde aparece este evento?</Label>
                    <Select value={newEvent.location || 'home'} onValueChange={(value) => setNewEvent({...newEvent, location: value})}>
                      <SelectTrigger className="bg-white dark:bg-slate-800" data-testid="event-location-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="home">🏠 Home (página de inicio)</SelectItem>
                        <SelectItem value="educacion">🎓 Educación (/educacion)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500">
                      Elige si el evento se muestra en la página de inicio o en la landing de Educación.
                    </p>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm">Botones de acción en la tarjeta</Label>
                    <Select value={newEvent.cta_mode || 'buttons'} onValueChange={(value) => setNewEvent({...newEvent, cta_mode: value})}>
                      <SelectTrigger className="bg-white dark:bg-slate-800" data-testid="event-cta-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buttons">Mostrar todo (Unirse, copiar, invitar, compartir)</SelectItem>
                        <SelectItem value="whatsapp">Solo botón de WhatsApp (ocultar lo demás)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500">
                      Elige si la tarjeta muestra todos los botones, o solo un botón de WhatsApp.
                    </p>
                    {newEvent.cta_mode === 'whatsapp' && (
                      <Input
                        type="text"
                        placeholder="Número WhatsApp (ej. 19548059212)"
                        value={newEvent.whatsapp_number || ''}
                        onChange={(e) => setNewEvent({...newEvent, whatsapp_number: e.target.value.replace(/[^0-9]/g, '')})}
                        className="bg-white dark:bg-slate-800"
                        data-testid="event-whatsapp-number-input"
                      />
                    )}
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm">Descripción (opcional)</Label>
                    <Textarea
                      placeholder="Descripción del evento..."
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                      className="bg-white dark:bg-slate-800 min-h-[60px]"
                    />
                  </div>
                </div>
                
                <Button
                  onClick={handleAddEvent}
                  disabled={eventLoading}
                  className="mt-4 bg-[#80C753] hover:bg-[#6ab043] text-white"
                >
                  {eventLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  {eventLoading ? 'Agregando...' : 'Agregar Evento'}
                </Button>
              </div>

              {/* Existing Events */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-[#1D1D1B] dark:text-white flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#45A0EE]" />
                  Eventos ({events.length})
                </h3>
                
                {events.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No hay eventos programados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {events.map((event) => (
                      editingEvent && editingEvent.id === event.id ? (
                        // Edit Form
                        <div
                          key={event.id}
                          className="p-4 rounded-lg border-2 border-[#45A0EE] bg-[#45A0EE]/5 dark:bg-[#45A0EE]/10"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-sm font-semibold text-[#45A0EE] flex items-center gap-2">
                              <Edit2 className="h-4 w-4" />
                              Editando Evento
                            </h4>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={handleCancelEdit}
                              className="h-7 w-7 text-slate-500 hover:text-slate-700"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1 md:col-span-2">
                              <Label className="text-xs">Título *</Label>
                              <Input
                                value={editingEvent.title}
                                onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})}
                                className="bg-white dark:bg-slate-800"
                              />
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-xs">Fecha *</Label>
                              <Input
                                type="date"
                                value={editingEvent.event_date}
                                onChange={(e) => setEditingEvent({...editingEvent, event_date: e.target.value})}
                                className="bg-white dark:bg-slate-800"
                              />
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-xs">Hora *</Label>
                              <Input
                                type="time"
                                value={editingEvent.event_time}
                                onChange={(e) => setEditingEvent({...editingEvent, event_time: e.target.value})}
                                className="bg-white dark:bg-slate-800"
                              />
                            </div>
                            
                            <div className="space-y-1 md:col-span-2">
                              <Label className="text-xs">Zona Horaria</Label>
                              <Select
                                value={editingEvent.timezone}
                                onValueChange={(value) => setEditingEvent({...editingEvent, timezone: value})}
                              >
                                <SelectTrigger className="bg-white dark:bg-slate-800">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {TIMEZONE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-1 md:col-span-2">
                              <Label className="text-xs">Plataforma *</Label>
                              <Select value={editingEvent.platform || 'zoom'} onValueChange={(value) => setEditingEvent({...editingEvent, platform: value})}>
                                <SelectTrigger className="bg-white dark:bg-slate-800">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="zoom">Zoom</SelectItem>
                                  <SelectItem value="tiktok">TikTok Live</SelectItem>
                                  <SelectItem value="youtube">YouTube Live</SelectItem>
                                  <SelectItem value="facebook">Facebook Live</SelectItem>
                                  <SelectItem value="instagram">Instagram Live</SelectItem>
                                  <SelectItem value="otro">Otro</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-1 md:col-span-2">
                              <Label className="text-xs">
                                {(editingEvent.platform || 'zoom') === 'zoom' ? 'Link de Zoom' :
                                 editingEvent.platform === 'tiktok' ? 'Link de TikTok' :
                                 editingEvent.platform === 'youtube' ? 'Link de YouTube' :
                                 editingEvent.platform === 'facebook' ? 'Link de Facebook' :
                                 editingEvent.platform === 'instagram' ? 'Link de Instagram' :
                                 'Link del Evento'} *
                              </Label>
                              <Input
                                value={editingEvent.zoom_link}
                                onChange={(e) => setEditingEvent({...editingEvent, zoom_link: e.target.value})}
                                className="bg-white dark:bg-slate-800"
                              />
                            </div>
                            
                            <div className="space-y-1 md:col-span-2">
                              <Label className="text-xs">Recurrencia (auto-rolling semanal)</Label>
                              <Select
                                value={editingEvent.recurrence || 'once'}
                                onValueChange={(value) => setEditingEvent({...editingEvent, recurrence: value})}
                              >
                                <SelectTrigger className="bg-white dark:bg-slate-800" data-testid="event-recurrence-edit">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="once">Una sola vez (fecha fija)</SelectItem>
                                  <SelectItem value="weekly_mon">Cada lunes (auto)</SelectItem>
                                  <SelectItem value="weekly_tue">Cada martes (auto)</SelectItem>
                                  <SelectItem value="weekly_wed">Cada miércoles (auto)</SelectItem>
                                  <SelectItem value="weekly_thu">Cada jueves (auto)</SelectItem>
                                  <SelectItem value="weekly_sat">Cada sábado (auto)</SelectItem>
                                  <SelectItem value="daily_weekdays">Diario Lun–Vie (viernes → lunes)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-1 md:col-span-2">
                              <Label className="text-xs">¿Dónde aparece?</Label>
                              <Select
                                value={editingEvent.location || 'home'}
                                onValueChange={(value) => setEditingEvent({...editingEvent, location: value})}
                              >
                                <SelectTrigger className="bg-white dark:bg-slate-800" data-testid="event-location-edit">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="home">🏠 Home (inicio)</SelectItem>
                                  <SelectItem value="educacion">🎓 Educación (/educacion)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-1 md:col-span-2">
                              <Label className="text-xs">Botones de acción en la tarjeta</Label>
                              <Select
                                value={editingEvent.cta_mode || 'buttons'}
                                onValueChange={(value) => setEditingEvent({...editingEvent, cta_mode: value})}
                              >
                                <SelectTrigger className="bg-white dark:bg-slate-800" data-testid="event-cta-mode-edit">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="buttons">Mostrar todo (Unirse, copiar, invitar, compartir)</SelectItem>
                                  <SelectItem value="whatsapp">Solo botón de WhatsApp (ocultar lo demás)</SelectItem>
                                </SelectContent>
                              </Select>
                              {(editingEvent.cta_mode === 'whatsapp') && (
                                <Input
                                  type="text"
                                  placeholder="Número WhatsApp (ej. 19548059212)"
                                  value={editingEvent.whatsapp_number || ''}
                                  onChange={(e) => setEditingEvent({...editingEvent, whatsapp_number: e.target.value.replace(/[^0-9]/g, '')})}
                                  className="bg-white dark:bg-slate-800 mt-1"
                                  data-testid="event-whatsapp-number-edit"
                                />
                              )}
                            </div>

                            <div className="space-y-1 md:col-span-2">
                              <Label className="text-xs">Descripción</Label>
                              <Textarea
                                value={editingEvent.description || ''}
                                onChange={(e) => setEditingEvent({...editingEvent, description: e.target.value})}
                                className="bg-white dark:bg-slate-800 min-h-[50px]"
                              />
                            </div>
                          </div>
                          
                          <div className="flex gap-2 mt-4">
                            <Button
                              onClick={handleSaveEvent}
                              disabled={eventLoading}
                              className="flex-1 bg-[#80C753] hover:bg-[#6ab043] text-white"
                            >
                              <Save className="h-4 w-4 mr-2" />
                              Guardar Cambios
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleCancelEdit}
                              className="border-slate-300"
                            >
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Normal Event Row
                        <div
                          key={event.id}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                            event.is_active 
                              ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50' 
                              : 'border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/20 opacity-60'
                          }`}
                        >
                          <Calendar className="h-4 w-4 text-[#45A0EE] flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1D1D1B] dark:text-white truncate">
                              {event.title}
                              {event.recurrence === 'weekly_mon' && (
                                <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">↻ Lunes</span>
                              )}
                              {event.recurrence === 'weekly_tue' && (
                                <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">↻ Martes</span>
                              )}
                              {event.recurrence === 'weekly_wed' && (
                                <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">↻ Miércoles</span>
                              )}
                              {event.recurrence === 'weekly_thu' && (
                                <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">↻ Jueves</span>
                              )}
                              {event.recurrence === 'weekly_sat' && (
                                <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">↻ Sábado</span>
                              )}
                              {event.recurrence === 'daily_weekdays' && (
                                <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300">↻ Lun–Vie diario</span>
                              )}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {event.event_date} {event.event_time}
                              </span>
                              {!event.is_active && (
                                <span className="text-amber-500">(Inactivo)</span>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleEventActive(event)}
                            data-testid={`event-toggle-visible-${event.id}`}
                            className={`h-8 px-2.5 gap-1.5 font-bold text-[11px] uppercase tracking-wide ${
                              event.is_active
                                ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                                : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/40'
                            }`}
                            title={event.is_active ? 'Está EN VIVO (visible). Click para ocultar' : 'Está OCULTO. Click para poner EN VIVO'}
                          >
                            {event.is_active ? (
                              <><Radio className="h-4 w-4" /> En vivo</>
                            ) : (
                              <><EyeOff className="h-4 w-4" /> Oculto</>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditEvent(event)}
                            className="h-8 w-8 text-[#80C753] hover:bg-[#80C753]/10"
                            title="Editar evento"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(event.zoom_link, '_blank')}
                            className="h-8 w-8 text-[#45A0EE]"
                            title="Abrir link de Zoom"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteEvent(event.id)}
                            className="h-8 w-8 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Eliminar evento"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Leads Tab — Hub unificado con sub-filtros */}
            <TabsContent value="leads" className="space-y-6">

              {/* ── Sub-filter pills ── */}
              <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-700 pb-3" data-testid="leads-subtabs">
                {[
                  { id: 'general', label: 'Prospectos generales', icon: Users, color: '#45A0EE', count: leads.length },
                  { id: 'orlando', label: 'Regalo Orlando', icon: Gift, color: '#c9a227', count: orlandoLeads.length },
                  { id: 'manual_free', label: 'Manual Free', icon: Sparkles, color: '#80C753', count: manualFreeLeads.length },
                  { id: 'opc', label: 'Registros OPC', icon: MapPin, color: '#c9a227', count: opcLeads.length },
                  { id: 'inversores', label: 'Inversores', icon: DollarSign, color: '#C9A84C', count: investorLeads.length },
                  { id: 'curso', label: 'Accesos al Curso', icon: Play, color: '#45A0EE', count: cursoAccess.length },
                ].map((tab) => {
                  const Icon = tab.icon;
                  const active = leadsSubTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      data-testid={`leads-subtab-${tab.id}`}
                      onClick={() => {
                        setLeadsSubTab(tab.id);
                        if (tab.id === 'orlando') loadLeads();
                        if (tab.id === 'inversores') loadInvestorLeads();
                        if (tab.id === 'curso') loadCursoAccess();
                        if (tab.id === 'manual_free') loadManualFreeLeads();
                        if (tab.id === 'opc') loadOpcLeads();
                        if (tab.id === 'general') loadLeads();
                      }}
                      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold transition-all ${
                        active
                          ? 'bg-[#1D1D1B] dark:bg-white text-white dark:text-[#1D1D1B] shadow-md'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" style={!active ? { color: tab.color } : {}} />
                      <span>{tab.label}</span>
                      <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full ${
                        active ? 'bg-white/20 text-white dark:bg-[#1D1D1B]/20 dark:text-[#1D1D1B]' : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300'
                      }`}>{tab.count}</span>
                    </button>
                  );
                })}
              </div>

              {/* ── Sub-vista: REGALO ORLANDO ── */}
              {leadsSubTab === 'orlando' && (
              <div data-testid="orlando-leads-section" className="border-2 border-[#c9a227]/30 rounded-xl p-4 bg-[#c9a227]/5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#1D1D1B] dark:text-white flex items-center gap-2">
                    <Gift className="h-4 w-4 text-[#c9a227]" />
                    Regalo Orlando — leads capturados ({orlandoLeads.length})
                  </h3>
                </div>
                {orlandoLeads.length === 0 ? (
                  <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                    Aún no hay leads del pop-up de Orlando. Aparecerán aquí cuando alguien lo complete.
                  </p>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-[#c9a227]/20 text-left text-slate-500 dark:text-slate-400">
                          <th className="py-2 px-2 font-semibold">Nombre</th>
                          <th className="py-2 px-2 font-semibold">Email</th>
                          <th className="py-2 px-2 font-semibold">País</th>
                          <th className="py-2 px-2 font-semibold">Teléfono</th>
                          <th className="py-2 px-2 font-semibold">Origen</th>
                          <th className="py-2 px-2 font-semibold">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orlandoLeads.map((l) => (
                          <tr key={l.id} data-testid={`orlando-lead-row-${l.id}`} className="border-b border-[#c9a227]/10 hover:bg-[#c9a227]/5">
                            <td className="py-2 px-2 font-medium text-[#1D1D1B] dark:text-white whitespace-nowrap">
                              {l.nombre} {l.apellido || ''}
                            </td>
                            <td className="py-2 px-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                              <a href={`mailto:${l.email}`} className="hover:underline text-[#45A0EE]">{l.email}</a>
                            </td>
                            <td className="py-2 px-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">{l.pais || '—'}</td>
                            <td className="py-2 px-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                              {l.telefono ? (
                                <a
                                  href={`https://wa.me/${l.telefono.replace(/[^0-9]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#25D366] hover:underline"
                                >
                                  {l.telefono}
                                </a>
                              ) : '—'}
                            </td>
                            <td className="py-2 px-2 text-slate-500 dark:text-slate-400 whitespace-nowrap">{l.source || 'home'}</td>
                            <td className="py-2 px-2 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                              {l.created_at ? new Date(l.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* ── Sub-vista: MANUAL FREE LEADS ── */}
              {leadsSubTab === 'manual_free' && (
              <div data-testid="manual-free-leads-section" className="border-2 border-[#80C753]/30 rounded-xl p-4 bg-[#80C753]/5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#1D1D1B] dark:text-white flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#80C753]" />
                    Manual Free — leads del acceso gratis ({manualFreeLeads.length})
                  </h3>
                  <button
                    type="button"
                    onClick={loadManualFreeLeads}
                    data-testid="manual-free-refresh"
                    className="text-xs font-bold text-[#45A0EE] hover:bg-[#45A0EE]/10 px-3 py-1.5 rounded-lg"
                  >
                    Refrescar
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 italic">
                  Reemplaza al funnel $27. Estos leads pidieron el manual gratis a cambio de su email/WhatsApp. Llamada de venta o seguimiento por WhatsApp para upsell al Empire $4,995.
                </p>
                {manualFreeLeads.length === 0 ? (
                  <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                    Aún no hay leads. Cuando alguien complete el form de <a href="/manual27" target="_blank" rel="noreferrer" className="text-[#45A0EE] underline">/manual27</a>, aparecerá aquí.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-[#80C753]/20 text-slate-500 dark:text-slate-400">
                          <th className="text-left py-2 px-2">Fecha</th>
                          <th className="text-left py-2 px-2">Nombre</th>
                          <th className="text-left py-2 px-2">Email</th>
                          <th className="text-left py-2 px-2">WhatsApp</th>
                          <th className="text-left py-2 px-2">País</th>
                          <th className="text-left py-2 px-2">Origen</th>
                          <th className="text-left py-2 px-2">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {manualFreeLeads.map((l, i) => (
                          <tr key={l.id || i} className="border-b border-slate-100 dark:border-slate-800 hover:bg-white/50 dark:hover:bg-slate-900/50">
                            <td className="py-2 px-2 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                              {l.created_at ? new Date(l.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '—'}
                            </td>
                            <td className="py-2 px-2 text-slate-700 dark:text-slate-200 font-semibold">{l.nombre || '—'}</td>
                            <td className="py-2 px-2 text-slate-600 dark:text-slate-300">
                              {l.email ? (
                                <a href={`mailto:${l.email}`} className="text-[#45A0EE] hover:underline">{l.email}</a>
                              ) : '—'}
                            </td>
                            <td className="py-2 px-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">{l.telefono || '—'}</td>
                            <td className="py-2 px-2 text-slate-600 dark:text-slate-300">{l.pais || '—'}</td>
                            <td className="py-2 px-2 text-slate-500 dark:text-slate-400 whitespace-nowrap">{l.source || 'manual-free'}</td>
                            <td className="py-2 px-2">
                              {l.telefono ? (
                                <a
                                  href={`https://wa.me/${l.telefono.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${l.nombre || ''}, gracias por solicitar el Manual de Club Viajemos. ¿Te gustaría que te muestre el sistema Empire?`)}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[#25D366] hover:bg-[#25D366]/10 px-2 py-1 rounded text-xs font-bold"
                                >
                                  <MessageCircle className="h-3 w-3" /> WA
                                </a>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* ── Sub-vista: REGISTROS OPC ── */}
              {leadsSubTab === 'opc' && (
              <div data-testid="opc-leads-section" className="border-2 border-[#c9a227]/30 rounded-xl p-4 bg-[#c9a227]/5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#1D1D1B] dark:text-white flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#c9a227]" />
                    Registros OPC — referidos de tours ({opcLeads.length})
                  </h3>
                  <button
                    type="button"
                    onClick={loadOpcLeads}
                    data-testid="opc-refresh"
                    className="text-xs font-bold text-[#45A0EE] hover:bg-[#45A0EE]/10 px-3 py-1.5 rounded-lg"
                  >
                    Refrescar
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 italic">
                  Referidos capturados en <a href="/registrosopc" target="_blank" rel="noreferrer" className="text-[#45A0EE] underline">/registrosopc</a>. Cada registro notifica por email al agente y al admin.
                </p>
                {opcLeads.length === 0 ? (
                  <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                    Aún no hay referidos OPC. Aparecerán aquí cuando un agente complete el formulario.
                  </p>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-[#c9a227]/20 text-left text-slate-500 dark:text-slate-400">
                          <th className="py-2 px-2 font-semibold">Fecha</th>
                          <th className="py-2 px-2 font-semibold">Agente</th>
                          <th className="py-2 px-2 font-semibold">Familia</th>
                          <th className="py-2 px-2 font-semibold">WhatsApp familia</th>
                          <th className="py-2 px-2 font-semibold">Destino</th>
                          <th className="py-2 px-2 font-semibold">Premio</th>
                          <th className="py-2 px-2 font-semibold">Calif.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {opcLeads.map((l, i) => (
                          <tr key={l.id || i} data-testid={`opc-lead-row-${l.id || i}`} className="border-b border-[#c9a227]/10 hover:bg-[#c9a227]/5 align-top">
                            <td className="py-2 px-2 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                              {l.created_at ? new Date(l.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '—'}
                            </td>
                            <td className="py-2 px-2 text-[#1D1D1B] dark:text-white">
                              <div className="font-semibold">{l.agente_nombre || '—'}</div>
                              <div className="text-slate-500">{l.agente_ciudad || ''}</div>
                              {l.agente_email && l.agente_email !== '—' ? (
                                <a href={`mailto:${l.agente_email}`} className="text-[#45A0EE] hover:underline">{l.agente_email}</a>
                              ) : null}
                            </td>
                            <td className="py-2 px-2 text-slate-700 dark:text-slate-200">
                              <div className="font-semibold">{l.persona1_nombre || l.familia_nombre || '—'}{l.persona1_edad ? ` (${l.persona1_edad})` : ''}</div>
                              {l.persona1_ocupacion || l.familia_ocupacion ? <div className="text-slate-500">💼 {l.persona1_ocupacion || l.familia_ocupacion}</div> : null}
                              {l.persona2_nombre ? <div className="text-slate-600 dark:text-slate-300 mt-1">🧑‍🤝‍🧑 {l.persona2_nombre}{l.persona2_edad ? ` (${l.persona2_edad})` : ''}{l.persona2_ocupacion ? ` · ${l.persona2_ocupacion}` : ''}</div> : null}
                              {l.persona2_telefono ? <div className="text-slate-500 text-[11px]">{l.persona2_telefono}</div> : null}
                              <div className="text-slate-500 mt-1">{l.adultos || 0} ad · {l.ninos || 0} niños · {l.fecha_viaje || '—'}{l.turno ? ` · ${l.turno}` : ''}</div>
                              {(l.documentos && l.documentos.length) ? <div className="text-slate-500 text-[11px]">🪪 {l.documentos.map(d => d === 'Tarjeta de crédito' && l.tipo_tarjeta ? `${d} (${l.tipo_tarjeta})` : d).join(', ')}</div> : null}
                              {l.observaciones ? <div className="text-amber-600 dark:text-amber-400 mt-0.5 max-w-[220px]" title={l.observaciones}>📝 {l.observaciones}</div> : null}
                            </td>
                            <td className="py-2 px-2">
                              {l.familia_telefono && l.familia_telefono !== '—' ? (
                                <a
                                  href={`https://wa.me/${String(l.familia_telefono).replace(/\D/g, '')}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[#25D366] hover:bg-[#25D366]/10 px-2 py-1 rounded font-bold whitespace-nowrap"
                                >
                                  <MessageCircle className="h-3 w-3" /> {l.familia_telefono}
                                </a>
                              ) : '—'}
                            </td>
                            <td className="py-2 px-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">{l.destino || '—'}</td>
                            <td className="py-2 px-2 text-slate-600 dark:text-slate-300 max-w-[180px]">{l.regalo || '—'}</td>
                            <td className="py-2 px-2 text-slate-500 dark:text-slate-400">{(l.calificaciones || []).length}/4</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* ── Sub-vista: PROSPECTOS GENERALES ── */}
              {leadsSubTab === 'general' && (
              <div className="space-y-6">

              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#1D1D1B] dark:text-white flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#45A0EE]" />
                  Prospectos ({leads.length})
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadLeads}
                  className="text-xs"
                >
                  Actualizar
                </Button>
              </div>

              {/* Filters */}
              {leads.length > 0 && (
                <div data-testid="leads-filters" className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Filter className="h-4 w-4 text-[#45A0EE]" />
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Filtros</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Origen</label>
                      <Select value={leadFilterOrigen} onValueChange={setLeadFilterOrigen}>
                        <SelectTrigger data-testid="filter-origen-select" className="h-8 text-xs bg-white dark:bg-slate-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          {filterOrigenes.map(o => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Agente referidor</label>
                      <Select value={leadFilterAgent} onValueChange={setLeadFilterAgent}>
                        <SelectTrigger data-testid="filter-agent-select" className="h-8 text-xs bg-white dark:bg-slate-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="none">Sin referido</SelectItem>
                          {filterAgents.map(agent => (
                            <SelectItem key={agent} value={agent}>{agent}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Plan</label>
                      <Select value={leadFilterPlan} onValueChange={setLeadFilterPlan}>
                        <SelectTrigger data-testid="filter-plan-select" className="h-8 text-xs bg-white dark:bg-slate-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          {filterPlans.map(plan => (
                            <SelectItem key={plan} value={plan}>{plan}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Estado pago</label>
                      <Select value={leadFilterStatus} onValueChange={setLeadFilterStatus}>
                        <SelectTrigger data-testid="filter-status-select" className="h-8 text-xs bg-white dark:bg-slate-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="pendiente">Pendiente</SelectItem>
                          <SelectItem value="completado">Pagado</SelectItem>
                          <SelectItem value="expirado">Expirado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {(leadFilterAgent !== 'all' || leadFilterPlan !== 'all' || leadFilterStatus !== 'all' || leadFilterOrigen !== 'all') && (
                    <button
                      data-testid="clear-filters-btn"
                      onClick={() => { setLeadFilterAgent('all'); setLeadFilterPlan('all'); setLeadFilterStatus('all'); setLeadFilterOrigen('all'); }}
                      className="mt-2 text-xs text-[#45A0EE] hover:underline"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              )}

              {(() => {
                const filteredLeads = leads.filter(lead => {
                  if (leadFilterAgent !== 'all') {
                    if (leadFilterAgent === 'none') {
                      if (lead.fue_referido) return false;
                    } else {
                      if (lead.nombre_agente_referidor !== leadFilterAgent) return false;
                    }
                  }
                  if (leadFilterPlan !== 'all' && lead.plan_seleccionado !== leadFilterPlan) return false;
                  if (leadFilterStatus !== 'all' && lead.estado_pago !== leadFilterStatus) return false;
                  if (leadFilterOrigen !== 'all' && (lead.origen || 'Precios') !== leadFilterOrigen) return false;
                  return true;
                });

                return filteredLeads.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{leads.length === 0 ? 'No hay prospectos registrados aún' : 'No hay resultados con los filtros seleccionados'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(leadFilterAgent !== 'all' || leadFilterPlan !== 'all' || leadFilterStatus !== 'all' || leadFilterOrigen !== 'all') && (
                      <p data-testid="filtered-count" className="text-xs text-slate-500">Mostrando {filteredLeads.length} de {leads.length} prospectos</p>
                    )}
                    {filteredLeads.map((lead) => (
                      <div
                        key={lead.id || lead.email}
                        data-testid={`lead-card-${lead.id || lead.email}`}
                        className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-[#1D1D1B] dark:text-white">
                              {lead.nombre} {lead.apellido}
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{lead.email}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            lead.estado_pago === 'completado' 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                              : lead.estado_pago === 'pendiente'
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                          }`}>
                            {lead.estado_pago === 'completado' ? 'Pagado' : lead.estado_pago === 'pendiente' ? 'Pendiente' : lead.estado_pago || 'Registrado'}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${
                            (lead.origen || 'Precios') === 'HOME MANUAL NEGOCIO'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                          }`}>
                            {lead.origen || 'Precios'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5 text-[#80C753]" />
                            <span className="text-slate-600 dark:text-slate-400">
                              Plan: <span className="font-medium text-[#1D1D1B] dark:text-white">{lead.plan_seleccionado}</span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5 text-[#80C753]" />
                            <span className="text-slate-600 dark:text-slate-400">
                              Monto: <span className="font-medium text-[#80C753]">${lead.monto}</span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">País: {lead.pais || '—'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Tel: {lead.telefono || '—'}</span>
                          </div>
                        </div>

                        {/* Referral info */}
                        <div className={`mt-3 p-2 rounded-lg text-sm ${
                          lead.fue_referido 
                            ? 'bg-[#80C753]/10 border border-[#80C753]/20' 
                            : 'bg-slate-50 dark:bg-slate-800'
                        }`}>
                          <div className="flex items-center gap-2">
                            <UserCheck className={`h-4 w-4 ${lead.fue_referido ? 'text-[#80C753]' : 'text-slate-400'}`} />
                            {lead.fue_referido ? (
                              <span className="text-[#1D1D1B] dark:text-white">
                                Referido por: <span className="font-bold text-[#80C753]">{lead.nombre_agente_referidor}</span>
                              </span>
                            ) : (
                              <span className="text-slate-400">Sin referido</span>
                            )}
                          </div>
                        </div>

                        {lead.created_at && (
                          <p className="text-xs text-slate-400 mt-2">
                            Registrado: {new Date(lead.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}

                        {/* Edit / Delete buttons */}
                        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                          <button
                            data-testid={`edit-lead-${lead.id || lead.email}`}
                            onClick={() => startEditLead(lead)}
                            className="flex items-center gap-1 text-xs text-[#45A0EE] hover:bg-[#45A0EE]/10 px-2.5 py-1.5 rounded-md transition-colors"
                          >
                            <Edit2 className="h-3 w-3" /> Editar
                          </button>
                          <button
                            data-testid={`delete-lead-${lead.id || lead.email}`}
                            onClick={() => deleteLead(lead.id || lead.email)}
                            className="flex items-center gap-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 px-2.5 py-1.5 rounded-md transition-colors"
                          >
                            <Trash2 className="h-3 w-3" /> Eliminar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Edit Lead Modal */}
              {editingLead && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingLead(null)}>
                  <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-bold text-[#1D1D1B] dark:text-white">Editar Lead</h3>
                      <button onClick={() => setEditingLead(null)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
                    </div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Nombre</Label>
                          <Input data-testid="edit-lead-nombre" value={editLeadData.nombre} onChange={e => setEditLeadData(p => ({ ...p, nombre: e.target.value }))} className="h-8 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs">Apellido</Label>
                          <Input data-testid="edit-lead-apellido" value={editLeadData.apellido} onChange={e => setEditLeadData(p => ({ ...p, apellido: e.target.value }))} className="h-8 text-sm" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input data-testid="edit-lead-email" value={editLeadData.email} onChange={e => setEditLeadData(p => ({ ...p, email: e.target.value }))} className="h-8 text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Telefono</Label>
                          <Input data-testid="edit-lead-telefono" value={editLeadData.telefono} onChange={e => setEditLeadData(p => ({ ...p, telefono: e.target.value }))} className="h-8 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs">Pais</Label>
                          <Input data-testid="edit-lead-pais" value={editLeadData.pais} onChange={e => setEditLeadData(p => ({ ...p, pais: e.target.value }))} className="h-8 text-sm" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Plan</Label>
                          <Input data-testid="edit-lead-plan" value={editLeadData.plan_seleccionado} onChange={e => setEditLeadData(p => ({ ...p, plan_seleccionado: e.target.value }))} className="h-8 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs">Estado Pago</Label>
                          <select data-testid="edit-lead-estado" value={editLeadData.estado_pago} onChange={e => setEditLeadData(p => ({ ...p, estado_pago: e.target.value }))} className="w-full h-8 text-sm border border-slate-200 dark:border-slate-600 rounded-md px-2 bg-white dark:bg-slate-700 dark:text-white">
                            <option value="pendiente">Pendiente</option>
                            <option value="completado">Pagado</option>
                            <option value="expirado">Expirado</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Agente Referidor</Label>
                        <Input data-testid="edit-lead-referidor" value={editLeadData.nombre_agente_referidor} onChange={e => setEditLeadData(p => ({ ...p, nombre_agente_referidor: e.target.value }))} className="h-8 text-sm" />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-5">
                      <Button data-testid="save-lead-edit" onClick={saveLeadEdit} className="flex-1 bg-[#80C753] hover:bg-[#6ab043] text-white text-sm">
                        <Save className="h-3.5 w-3.5 mr-1" /> Guardar
                      </Button>
                      <Button variant="outline" onClick={() => setEditingLead(null)} className="text-sm">Cancelar</Button>
                    </div>
                  </div>
                </div>
              )}
              </div>
              )}

              {/* ── Sub-vista: INVERSORES ── */}
              {leadsSubTab === 'inversores' && (
              <div className="bg-white dark:bg-[#1F2937] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700" data-testid="leads-inversores-section">
                <h3 className="text-lg font-bold text-[#1D1D1B] dark:text-white mb-1 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-[#C9A84C]" />
                  Leads de Inversores — ({investorLeads.length})
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  Solicitudes capturadas desde Kit Inversor, página de Inversores y Socios Fundadores.
                </p>

                {investorLeads.length === 0 ? (
                  <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                    <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>Aún no hay solicitudes de inversores.</p>
                    <p className="text-xs mt-2">Las solicitudes desde los formularios HTML aparecerán aquí.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-[#0F172A] text-slate-700 dark:text-slate-300">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Fecha</th>
                          <th className="px-3 py-2 text-left font-semibold">Nombre</th>
                          <th className="px-3 py-2 text-left font-semibold">Email</th>
                          <th className="px-3 py-2 text-left font-semibold">Teléfono</th>
                          <th className="px-3 py-2 text-left font-semibold">País</th>
                          <th className="px-3 py-2 text-left font-semibold">Monto</th>
                          <th className="px-3 py-2 text-left font-semibold">Origen</th>
                          <th className="px-3 py-2 text-left font-semibold">Mensaje</th>
                          <th className="px-3 py-2 text-left font-semibold">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {investorLeads.map((l) => (
                          <tr key={l.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                              {l.created_at ? new Date(l.created_at).toLocaleString('es-419', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                            </td>
                            <td className="px-3 py-2 font-medium text-[#1D1D1B] dark:text-white">{l.nombre || '—'}</td>
                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                              <a href={`mailto:${l.email}`} className="hover:underline">{l.email}</a>
                            </td>
                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{l.telefono || '—'}</td>
                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{l.pais || '—'}</td>
                            <td className="px-3 py-2 text-[#C9A84C] font-semibold">{l.monto_interes ? `$${l.monto_interes}` : '—'}</td>
                            <td className="px-3 py-2">
                              <span className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-[#C9A84C]/20 text-[#8A6B1A] rounded uppercase">
                                {l.source || 'kit-inversor'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400 max-w-[220px] truncate" title={l.mensaje}>
                              {l.mensaje || '—'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {l.telefono && (
                                <a
                                  href={`https://wa.me/${l.telefono.replace(/[^\d]/g, '')}?text=${encodeURIComponent(`Hola ${l.nombre}, soy del equipo ClubViajemos y vi tu interés en la oferta para inversores.`)}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid={`investor-wa-${l.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-[#25D366] text-white rounded hover:bg-[#1eb255] transition-colors"
                                >
                                  WA
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* ── Sub-vista: ACCESOS AL CURSO ── */}
              {leadsSubTab === 'curso' && (
              <div className="bg-white dark:bg-[#1F2937] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700" data-testid="leads-curso-section">
                <h3 className="text-lg font-bold text-[#1D1D1B] dark:text-white mb-1 flex items-center gap-2">
                  <Play className="h-5 w-5 text-[#45A0EE]" />
                  Accesos al Curso — ({cursoAccess.length})
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  Personas que ingresaron al gate de <code>/curso/videos</code> con sus datos de contacto.
                </p>

                {cursoAccess.length === 0 ? (
                  <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                    <Play className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>Aún no hay accesos registrados al curso.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-[#0F172A] text-slate-700 dark:text-slate-300">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Primer acceso</th>
                          <th className="px-3 py-2 text-left font-semibold">Último acceso</th>
                          <th className="px-3 py-2 text-left font-semibold">Nombre</th>
                          <th className="px-3 py-2 text-left font-semibold">Email</th>
                          <th className="px-3 py-2 text-left font-semibold">Teléfono</th>
                          <th className="px-3 py-2 text-left font-semibold">País</th>
                          <th className="px-3 py-2 text-center font-semibold">Visitas</th>
                          <th className="px-3 py-2 text-left font-semibold">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {cursoAccess.map((u) => (
                          <tr key={u.id || u.email} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                              {u.first_access ? new Date(u.first_access).toLocaleString('es-419', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                              {u.last_access ? new Date(u.last_access).toLocaleString('es-419', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                            </td>
                            <td className="px-3 py-2 font-medium text-[#1D1D1B] dark:text-white">
                              {u.nombre} {u.apellido || ''}
                            </td>
                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                              <a href={`mailto:${u.email}`} className="hover:underline">{u.email}</a>
                            </td>
                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{u.telefono || '—'}</td>
                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{u.pais || '—'}</td>
                            <td className="px-3 py-2 text-center">
                              <span className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-[#45A0EE]/20 text-[#1D6FB8] rounded">
                                {u.visits || 1}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {u.telefono && (
                                <a
                                  href={`https://wa.me/${u.telefono.replace(/[^\d]/g, '')}?text=${encodeURIComponent(`Hola ${u.nombre}, soy del equipo ClubViajemos. Vi que entraste al curso y queria saber si tienes alguna duda.`)}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid={`curso-wa-${u.id || u.email}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-[#25D366] text-white rounded hover:bg-[#1eb255] transition-colors"
                                >
                                  WA
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

            </TabsContent>

            {/* Codes Tab */}
            <TabsContent value="codes" className="space-y-6">
              <CodesTab
                discountCodes={discountCodes}
                codeQuantity={codeQuantity}
                setCodeQuantity={setCodeQuantity}
                codeAmount={codeAmount}
                setCodeAmount={setCodeAmount}
                generateCodes={generateCodes}
                generatingCodes={generatingCodes}
              />
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos" className="space-y-6">
              <VideosTab
                newTutorial={newTutorial}
                setNewTutorial={setNewTutorial}
                handleAddTutorial={handleAddTutorial}
                tutorialLoading={tutorialLoading}
                tutorials={tutorials}
                groupedTutorials={groupedTutorials}
                handleDeleteTutorial={handleDeleteTutorial}
                secretosOptions={SECRETOS_OPTIONS}
                handleThumbnailUpload={handleThumbnailUpload}
                thumbnailUploading={thumbnailUploading}
              />
            </TabsContent>

            {/* Agentes Tab */}
            <TabsContent value="agentes" className="space-y-6">
              <AgentesTab
                newAgente={newAgente}
                setNewAgente={setNewAgente}
                createAgente={createAgente}
                agentes={agentes}
                deleteAgente={deleteAgente}
                loadAgentes={loadAgentes}
              />
            </TabsContent>

            {/* CODIGOS $500 TAB */}
            <TabsContent value="codigos500" className="space-y-6">
              <Codigos500Tab codigos500={codigos500} />
            </TabsContent>

            {/* Mural Informativo Tab — eliminado */}


            {/* Funnel $27 tab removed — replaced by Manual Free flow */}
          </Tabs>
    );

    // Full page mode - render directly
    if (renderMode === 'page') {
      if (!isAuthenticated) {
        return (
          <div className="max-w-sm mx-auto mt-10 bg-white dark:bg-[#151B2B] border border-slate-200 dark:border-slate-700 rounded-2xl p-8 shadow-sm" data-testid="admin-login-gate">
            <div className="w-14 h-14 rounded-2xl bg-[#0B1F44] flex items-center justify-center mx-auto mb-4">
              <Lock className="h-7 w-7 text-[#D4AF37]" />
            </div>
            <h2 className="text-lg font-black text-center text-[#1D1D1B] dark:text-white mb-1">Acceso restringido</h2>
            <p className="text-xs text-center text-slate-500 dark:text-slate-400 mb-6">Ingresa la contraseña de administración para continuar.</p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
              placeholder="Contraseña"
              autoFocus
              data-testid="admin-login-password"
              className="w-full px-4 py-2.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 mb-3"
            />
            <button
              onClick={handleLogin}
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D4AF37] text-[#1A1A2E] font-bold rounded-lg hover:bg-[#B8860B] transition-colors text-sm disabled:opacity-60"
            >
              <Lock className="h-4 w-4" /> {loading ? 'Verificando…' : 'Ingresar'}
            </button>
          </div>
        );
      }
      return adminContent;
    }

    // Sheet mode
    return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-white dark:bg-[#151B2B] border-slate-200 dark:border-slate-700 overflow-y-auto p-0">
        <div className="p-6">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-[#1D1D1B] dark:text-white flex items-center gap-2">
              <Lock className="h-5 w-5 text-[#45A0EE]" />
              Panel de Administración
            </SheetTitle>
            <SheetDescription className="text-slate-500 dark:text-slate-400">
              Gestiona eventos, leads, códigos y videos
            </SheetDescription>
          </SheetHeader>
          {adminContent}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AdminPanel;
